import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '@/api/errors';
import {
  fetchVorderingen,
  createVordering,
  markFasesGevorderd,
  deleteVordering,
  getShareLink,
  previewFotoRapport,
  type VorderingenResponse,
  type AllCollector,
  type Vordering,
  type SaleOrderBreakdown,
  type LineBillingEntry,
} from '@/api/vorderingen';
import type { DashboardFase } from '@/api/projectFases';
import type { VorderingSummaryData } from '@/utils/dashboardSummaries';
import { fetchProjectInterventiePhotos } from '@/api/interventie';
import { OmschrijvingOptions } from './vordering/OmschrijvingOptions';
import { buildOmschrijving, fmtDateShort, fotoCount, groupInterventieByRepair, titleInterventieGroups, wholePercent, type InterventiePhotoGroup } from './vordering/vorderingHelpers';
import { LineBillingPicker, closesOutAllLines, defaultLineEntries, emptyLineEntry, hasHeadroom, lineBillingPreview, type LineEntry } from './vordering/LineBillingPicker';
import { EindeWerkenPrompt } from './vordering/EindeWerkenPrompt';
import { M2BasisNotice } from './vordering/M2BasisNotice';
import { PhotoMailPrompt, PhotoMailCardLines, type PhotoMailOpener } from './vordering/PhotoMailPrompt';
import { PlanningFlipPreview } from './vordering/PlanningFlipPreview';
import { eligibleFlipIdsOf, selectedFasesForFlip } from './vordering/faseFlip';
import { resolveM2Basis } from '@/utils/m2Basis';
import { usePermissionsStore } from '@/stores/permissions';
import { usePlanningStore } from '@/stores/planning';
import { useProjectViewStore } from '@/stores/projectView';

interface DashboardVorderingSectionProps {
  projectId: number;
  /** All project fases (id, name, date_deadline) — drives grouping order + the
   *  planning-flip date gate. The view is project-level (cross-fase). */
  fases: DashboardFase[];
  /** Dashboard's authoritative collector→fase map (materiaallijst-based). Kept for
   *  parity with the rest of the dashboard, though grouping prefers the GET's
   *  picking-based fase_id on each all_collectors row. */
  collectorFases: Record<number, number>;
  /** Re-fetch the fase mapping after a create changes collector→fase. */
  onChanged?: () => void;
  /** Bumped by the parent on any collector mutation (edit/create/delete/photo). The
   *  overview's selectable rows come from this section's OWN fetch (all_collectors),
   *  so without an external reload signal a collector just flipped to «op druk»
   *  wouldn't appear as selectable until a full page reload. On change → silent reload. */
  refreshSignal?: number;
  /** Bumped by the parent when interventie photos are uploaded/deleted in the
   *  DashboardInterventieSection. Re-fetches the interventie date-groups so a freshly
   *  added group becomes selectable-to-vorderen (and a fully-deleted one disappears)
   *  without a full page reload. Separate from `refreshSignal` so an interventie photo
   *  change doesn't needlessly reload the whole vorderingen payload. */
  interventieRefreshSignal?: number;
  /** Report the contract's fully-invoiced flag up so the status line can show it
   *  without a duplicate fetch. Called after each successful load. */
  onFullyInvoicedChange?: (fullyInvoiced: boolean) => void;
  /** Report the contract netto + gefactureerd-% up so the collapsed section
   *  header can show the headline figures without a duplicate fetch. Called
   *  with null until the first load resolves. */
  onSummaryChange?: (summary: VorderingSummaryData | null) => void;
  /** Rendered inside a DashboardSection coloured shell — drop our own outer card
   *  chrome (border / radius / shadow / padding) and the duplicated "Vordering"
   *  title (the shell owns both). Sub-context (scope badge, contract caption) stays. */
  embedded?: boolean;
}

/** One bordered fase card in the cross-fase overview. */
interface FaseGroup {
  /** null = the "Geen fase" bucket (collectors with no resolved fase). */
  id: number | null;
  name: string;
  /** Planned date (date_deadline) — null for undated/Geen-fase groups. */
  date: string | null;
  /** date > vandaag → toekomst (amber framing, "blijft ongewijzigd" — niet geflipt). */
  isFuture: boolean;
  rows: AllCollector[];
  gevorderdCount: number;
  total: number;
  gevorderdM2: number;
}

const fmtEur = (n: number): string =>
  new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(n || 0);

/** yyyy-mm-dd → dd/mm/yyyy (no toISOString — avoids the CET date-shift). */
const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : String(iso);
};

/** yyyy-mm-dd[ hh:mm:ss] → dd-mm-yyyy (per-SO breakdown date, matches mockup). */
const fmtDateDash = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return d && m && y ? `${d}-${m}-${y}` : String(iso);
};

/**
 * Today as yyyy-mm-dd from LOCAL date components — never toISOString (it shifts
 * the date back a day in CET/CEST). Mirrors dashboardFases.ts:13-15. Used only
 * for the planning-flip date gate (drives both the create-time mark write and the preview).
 */
const localTodayStr = (): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  const t = new Date();
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`;
};

/**
 * x_status → Dutch badge label.
 * NOTE: the mockup calls `goedgekeurd` "Verstuurd"; we use "Goedgekeurd" to match
 * the Odoo selection value — wording is provisional and may change.
 */
const STATUS_LABEL: Record<string, string> = {
  concept: 'Concept',
  goedgekeurd: 'Goedgekeurd',
  gefactureerd: 'Gefactureerd',
};
const statusLabel = (status: string): string => STATUS_LABEL[status] ?? status;
const statusBadgeClass = (status: string): string =>
  status === 'gefactureerd'
    ? 'bg-td-green/10 text-td-green'
    : status === 'goedgekeurd'
      ? 'bg-td-blue/10 text-td-blue'
      : 'bg-gray-100 text-td-muted';

/**
 * Photo-upload status (`fotos_uitvoering`) → secondary, purely-informational badge.
 * Op druk stays the only billing gate — this never blocks selection or submit.
 * Returns null for missing/empty/unknown values (renders no badge).
 */
const FOTO_BADGE: Record<string, { label: string; className: string }> = {
  Geen_Fotos: { label: "geen foto's", className: 'bg-amber-100 text-amber-800' },
  Fotos_Geupload: { label: 'geüpload', className: 'bg-td-blue/10 text-td-blue' },
  Fotos_Goedgekeurd: { label: 'goedgekeurd', className: 'bg-td-green/10 text-td-green' },
};
const fotoBadge = (status: string | null | undefined) => (status ? FOTO_BADGE[status] ?? null : null);

/**
 * SO contract-role → tag label + colour (matches the mockup `.tag.{basis,meerprijs,minprijs}`):
 * Basis blue, Meerprijs purple, Minprijs red.
 */
const SO_ROLE_TAG: Record<SaleOrderBreakdown['role'], { label: string; className: string }> = {
  basis: { label: 'Basis', className: 'bg-td-blue/10 text-td-blue' },
  meerprijs: { label: 'Meerprijs', className: 'bg-td-purple/10 text-td-purple' },
  minprijs: { label: 'Minprijs', className: 'bg-td-magenta/10 text-td-magenta' },
};

/**
 * Project-level cross-fase client-billing (vordering) section for the dashboard
 * (Fase 9b). Inline — no modal. Rebuilt to mockups/vordering-overzicht.html: every
 * collector grouped by fase, Gevorderd vs Niet-gevorderd, cross-fase multi-select,
 * + a planning-flip preview. On create the today/past fases of the selected
 * collectors are auto-marked «gevorderd» (the last one «einde werken» when that
 * toggle is on) — ADR-064, superseding ADR-052 "guide-only" for this flow.
 * Reuses the existing vordering API; the billing-safety guards (terminale lock,
 * per-SO pinning, TOCTOU) live server-side (Fase 9a).
 */
export function DashboardVorderingSection({ projectId, fases, collectorFases, refreshSignal, interventieRefreshSignal, onChanged, onFullyInvoicedChange, onSummaryChange, embedded = false }: DashboardVorderingSectionProps) {
  const canWrite = usePermissionsStore((s) => s.canFeature)('vordering_create');
  // Planning refresh — so a create's «gevorderd»/«einde werken» flip and a delete's
  // revert (backend resets uncovered fases → "In uitvoering") show instantly.
  // refreshProject drives the project tasks that carry the planningstage badge
  // (PlanningTab / TaskDetailPanel). fetchPlanning drives the cross-project scheduler
  // board (only when already loaded; silent = no loading flash, labels swap in place).
  const refreshProject = useProjectViewStore((s) => s.refreshProject);
  const refreshPlanning = usePlanningStore((s) => s.fetchPlanning);
  const planningLoaded = usePlanningStore((s) => s.tasks.length > 0);

  const [data, setData] = useState<VorderingenResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ----- Create-panel form state -----
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // Interventie date-groups selectable into this vordering (mirrors collector selection).
  // The photos live in DashboardInterventieSection; here we only fetch the date-groups so
  // a PL can fold a repair day's photos into the vordering — no kringen/m², just the keys.
  const [interventieGroups, setInterventieGroups] = useState<InterventiePhotoGroup[]>([]);
  const [selectedInterventieDates, setSelectedInterventieDates] = useState<Set<string>>(new Set());
  const [eindeWerken, setEindeWerken] = useState(false);
  // Line-billing picker: which sale-order line(s) this vordering bills against —
  // each CHECKED line owns its own {percentage, description} entry. Default
  // selection (exactly-one-with-headroom pre-checked, two-or-more none — or,
  // under einde werken, EVERY headroom line) is applied once per load via
  // linesInitializedRef — later reloads (refreshSignal) only prune entries for
  // ids that lost their headroom.
  const [lineEntries, setLineEntries] = useState<Map<number, LineEntry>>(new Map());
  const [linesTouched, setLinesTouched] = useState(false);
  const linesInitializedRef = useRef(false);
  // Tracks eindeWerken's PREVIOUS value so the re-seed effect below only fires
  // on an off→on transition (never on→off, which must leave the PL's own
  // selection alone) — the toggle can flip after the form (and lineEntries) is
  // already populated, so the initial-load default alone isn't enough.
  const prevEindeWerkenRef = useRef(false);
  // Ondertekenaar is free text only (naam/e-mail), empty by default; the contacts
  // dropdown FILLS this field — it is never pre-selected (drops the old default_signer_id).
  const [signerName, setSignerName] = useState('');
  // The contact picked in the dropdown, for the photo-mail prompt only (never sent with
  // the vordering): null = none picked (or the name was typed by hand afterwards);
  // { email: null } = a contact WITHOUT email was picked, so the prompt starts empty.
  const [signerContact, setSignerContact] = useState<{ email: string | null } | null>(null);
  // The photo-mail prompt: right after create, or from a card's "Foto's versturen".
  const [photoMailFor, setPhotoMailFor] = useState<{ id: number; opener: PhotoMailOpener } | null>(null);
  const [photoMailRefresh, setPhotoMailRefresh] = useState(0);
  const [customName, setCustomName] = useState('');
  const [omschrijving, setOmschrijving] = useState('');
  const [lastAutoOmschrijving, setLastAutoOmschrijving] = useState('');
  // «m² / LP mee op factuur?» — off by default, not stored (work item vordering-lp-option-and-installer-photos).
  const [m2OpFactuur, setM2OpFactuur] = useState(false);
  const [lpOpFactuur, setLpOpFactuur] = useState(false);
  // Auto-naam tracking (mirrors lastAutoOmschrijving) so a manually-typed naam survives toggles.
  const [lastAutoName, setLastAutoName] = useState('');
  const [notes, setNotes] = useState('');

  // Foto rapport preview
  const [fotoKey, setFotoKey] = useState<string | null>(null);
  const [fotoUrl, setFotoUrl] = useState<string | null>(null);
  const [fotoZipUrl, setFotoZipUrl] = useState<string | null>(null);
  const [fotoSelectionIds, setFotoSelectionIds] = useState<number[]>([]);
  // Interventie dates snapshot at the moment the preview was generated — so the
  // preview invalidates when the interventie selection changes, not only the collectors.
  const [fotoInterventieDates, setFotoInterventieDates] = useState<string[]>([]);
  const [includeFotoLink, setIncludeFotoLink] = useState(true);
  const [fotoGenerating, setFotoGenerating] = useState(false);
  const [fotoError, setFotoError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // "Alles gevorderd — is dit einde werken?" — open while the PL still has to
  // answer that question for a submit that closes out every offertelijn with the
  // type toggle on «Gevorderd». Both answers then create the vordering.
  const [eindeWerkenAsk, setEindeWerkenAsk] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Non-fatal: the vordering succeeded but the automatic planning-flip failed.
  const [submitWarning, setSubmitWarning] = useState<string | null>(null);
  const [actionId, setActionId] = useState<number | null>(null);
  // Delete failures get their OWN inline error — NOT the fatal `error` above, which
  // would collapse the whole section behind a misleading "laden mislukt".
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [shareState, setShareState] = useState<{ id: number; status: 'copied' | 'error' } | null>(null);

  const omschrijvingRef = useRef<HTMLTextAreaElement | null>(null);
  const projectIdRef = useRef(projectId);

  const load = useCallback(() => {
    const pid = projectId;
    projectIdRef.current = pid;
    setLoading(true);
    setError(null);
    fetchVorderingen(pid)
      .then((res) => {
        // Drop a stale response after a project switch (React 19 makes a
        // setState-after-unmount a safe no-op, so no alive-guard is needed —
        // an earlier alive-ref broke StrictMode's double-mount → stuck loading).
        if (projectIdRef.current !== pid) return;
        setData(res);
        const initialName = `Vordering ${res.next_sequence}`;
        setCustomName(initialName);
        setLastAutoName(initialName);
        // Prune any now-stale selection: a refetch (delete / onChanged) can flip a
        // previously-selected collector to vordered, dropping it from the selectable
        // set (available_collectors). Keeping its id would skew the header count vs.
        // the selection summary and submit a phantom id. Intersect with the fresh ids.
        const selectableIds = new Set(res.available_collectors.map((c) => c.id));
        setSelected((prev) => {
          const next = new Set([...prev].filter((id) => selectableIds.has(id)));
          return next.size === prev.size ? prev : next;
        });
        // Line-billing entries: default them ONCE per load (mirrors the collector
        // prune above but only applies the exactly-one-with-headroom default on
        // the FIRST load — a later reload from refreshSignal must not clobber a
        // PL's in-progress entries, it only drops ids that lost their headroom).
        const billableIds = new Set(res.billable_lines.filter((l) => l.amount - l.billed > 0.005).map((l) => l.id));
        setLineEntries((prev) => {
          if (!linesInitializedRef.current) {
            linesInitializedRef.current = true;
            return defaultLineEntries(res.billable_lines);
          }
          const next = new Map([...prev].filter(([id]) => billableIds.has(id)));
          return next.size === prev.size ? prev : next;
        });
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (projectIdRef.current !== pid) return;
        setError(err instanceof Error ? err.message : 'Vorderingen laden mislukt');
        setLoading(false);
      });
  }, [projectId]);

  useEffect(() => {
    setSelected(new Set());
    setSelectedInterventieDates(new Set());
    setEindeWerken(false);
    setSignerName('');
    setSignerContact(null);
    setNotes('');
    setOmschrijving('');
    setLastAutoOmschrijving('');
    setM2OpFactuur(false);
    setLpOpFactuur(false);
    setFotoKey(null);
    setFotoUrl(null);
    setFotoZipUrl(null);
    setFotoSelectionIds([]);
    setFotoInterventieDates([]);
    setIncludeFotoLink(true);
    setFotoError(null);
    setSubmitError(null);
    setDeleteError(null);
    setShareState(null);
    setLineEntries(new Map());
    setLinesTouched(false);
    setEindeWerkenAsk(false);
    linesInitializedRef.current = false;
    prevEindeWerkenRef.current = false;
    load();
  }, [load]);

  // Reload the overview (NOT the form) when the parent signals a collector mutation —
  // e.g. a collector flipped to «op druk» must appear as selectable-to-vorderen
  // immediately, without a full page reload. load() refreshes all_collectors/
  // available_collectors silently (data already set → no loading flash) and prunes any
  // now-stale selection. Skip the initial mount: load() already ran via the effect above.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    load();
    // Key only on refreshSignal: `load` changes identity on project switch, which the
    // mount effect above already handles — depending on it here would double-fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  // Fetch the interventie date-groups (project-level). Re-runs on project switch and
  // whenever the parent bumps `interventieRefreshSignal` (a photo uploaded/deleted in
  // the DashboardInterventieSection), so a new repair-day group becomes selectable and
  // an emptied one disappears without a full reload. Prunes any now-stale selected date
  // against the fresh groups — mirrors load()'s collector-selection prune, and is what
  // makes the empty-€0-vordering bug impossible (a lingering date can't survive here).
  useEffect(() => {
    // Only write roles render the interventie block — skip the fetch for read-only
    // viewers (it would 403 for roles without photos:read, and is unused regardless).
    if (!canWrite) { setInterventieGroups([]); return; }
    const pid = projectId;
    fetchProjectInterventiePhotos(pid)
      .then((photos) => {
        if (projectIdRef.current !== pid) return;
        const groups = groupInterventieByRepair(photos); // per repair AND day (Igor, 2026-09-29)
        setInterventieGroups(groups);
        const dates = new Set(groups.map((g) => g.id));
        setSelectedInterventieDates((prev) => {
          const next = new Set([...prev].filter((d) => dates.has(d)));
          return next.size === prev.size ? prev : next;
        });
      })
      .catch(() => {
        // Non-fatal: interventie is an optional add-on to a vordering. On error just
        // show no groups (the section is still fully usable for collectors).
        if (projectIdRef.current !== pid) return;
        setInterventieGroups([]);
      });
  }, [projectId, interventieRefreshSignal, canWrite]);

  // Abort any in-flight foto-rapport preview on unmount.
  useEffect(() => () => { abortRef.current?.abort(); }, []);

  // Cross-fase overview: every op-druk collector grouped by its authoritative
  // (picking-based) fase_id from all_collectors. Non-op-druk collectors are hidden
  // (earlier decision — billing gate is op druk). Fases are ordered by date_deadline
  // (from the `fases` prop); a "Geen fase" bucket (faseId === null) catches
  // unresolved collectors so nothing vanishes. Each group carries its planned date
  // + a Gevorderd/totaal rollup with the gevorderd € sum.
  const faseGroups = useMemo<FaseGroup[]>(() => {
    const collectors = (data?.all_collectors ?? []).filter((c) => c.collector_op_druk);
    // Authoritative fase per row: GET's picking-based fase_id, falling back to the
    // dashboard map only when the GET left it unresolved.
    const faseOf = (c: AllCollector): number | null => c.fase_id ?? collectorFases[c.id] ?? null;

    // Bucket collectors by fase id (null → "Geen fase").
    const buckets = new Map<number | null, AllCollector[]>();
    for (const c of collectors) {
      const fid = faseOf(c);
      const arr = buckets.get(fid) ?? [];
      arr.push(c);
      buckets.set(fid, arr);
    }

    // Order: known fases by date_deadline (undated last), then "Geen fase" bucket.
    const orderedFaseIds = [...fases]
      .sort((a, b) => {
        const da = String(a.date_deadline || '').slice(0, 10);
        const db = String(b.date_deadline || '').slice(0, 10);
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return da.localeCompare(db);
      })
      .map((f) => f.id);

    const today = localTodayStr();
    const buildGroup = (
      id: number | null,
      name: string,
      date: string | null,
      rows: AllCollector[],
    ): FaseGroup => {
      const gevorderdCount = rows.filter((c) => c.vordered).length;
      const gevorderdM2 = rows.filter((c) => c.vordered).reduce((s, c) => s + (c.collector_m2 || 0), 0);
      const isFuture = !!date && date.slice(0, 10) > today;
      return {
        id,
        name,
        date,
        isFuture,
        rows: rows.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
        gevorderdCount,
        total: rows.length,
        gevorderdM2,
      };
    };

    const groups: FaseGroup[] = [];
    for (const fid of orderedFaseIds) {
      const rows = buckets.get(fid);
      if (!rows || rows.length === 0) continue;
      const fase = fases.find((f) => f.id === fid);
      groups.push(buildGroup(fid, fase?.name ?? `Fase ${fid}`, fase?.date_deadline ?? null, rows));
    }
    // Any fase id present on collectors but absent from `fases` (defensive) +
    // the null bucket.
    for (const [fid, rows] of buckets) {
      if (fid !== null && orderedFaseIds.includes(fid)) continue;
      if (fid === null) continue;
      const fase = fases.find((f) => f.id === fid);
      groups.push(buildGroup(fid, fase?.name ?? `Fase ${fid}`, fase?.date_deadline ?? null, rows));
    }
    const geenFase = buckets.get(null);
    if (geenFase && geenFase.length > 0) {
      groups.push(buildGroup(null, 'Geen fase', null, geenFase));
    }
    return groups;
  }, [data, collectorFases, fases]);

  const fullyInvoiced = data?.fully_invoiced ?? false;

  // Surface fully-invoiced up to the dashboard status line (no duplicate fetch).
  useEffect(() => {
    onFullyInvoicedChange?.(fullyInvoiced);
  }, [fullyInvoiced, onFullyInvoicedChange]);

  // The create-form helpers (omschrijving, estimate, foto-rapport) reuse the
  // billable `available_collectors` shape — selectable overview rows are op-druk,
  // not-yet-vordered collectors, which are exactly what available_collectors holds.
  const selectedCollectors = useMemo(
    () => (data?.available_collectors ?? []).filter((c) => selected.has(c.id)),
    [data, selected],
  );

  // key → the vordering it's already in (from the GET). Drives the «gevorderd» badge.
  const interventieVorderedMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const iv of data?.interventie_vordered ?? []) m.set(iv.key, iv.vordering_name);
    return m;
  }, [data]);

  // Each date-group split into already-vordered vs still-open photos — mirrors the
  // collector gevorderd/niet-gevorderd split. A group with open photos is selectable;
  // a fully-vordered group shows a «gevorderd» badge and can't be re-selected.
  const interventieGroupViews = useMemo(
    () => titleInterventieGroups(interventieGroups, fases).map((g) => {
      const openKeys: string[] = [];
      const names = new Set<string>();
      for (const k of g.keys) {
        const vn = interventieVorderedMap.get(k);
        if (vn !== undefined) names.add(vn); else openKeys.push(k);
      }
      return { id: g.id!, date: g.date, label: g.label, title: g.title!, faseId: g.faseId, total: g.keys.length, openKeys, vorderingNames: [...names] };
    }),
    [interventieGroups, interventieVorderedMap, fases],
  );

  // Selected groups that still have OPEN photos → the interventie keys to bill (open
  // only, never re-billing an already-vordered photo). Derived (never the raw Set):
  // `selectedIntGroups.length > 0` is the authoritative "has billable interventie" gate,
  // so a stale/fully-vordered selected date resolves to nothing. Shaped for buildOmschrijving.
  const selectedIntGroups = useMemo(
    () => interventieGroupViews
      .filter((g) => selectedInterventieDates.has(g.id) && g.openKeys.length > 0)
      .map((g) => ({ id: g.id, date: g.date, label: g.label, title: g.title, faseId: g.faseId, count: g.openKeys.length, keys: g.openKeys })),
    [interventieGroupViews, selectedInterventieDates],
  );
  const interventiePhotoKeys = useMemo(
    () => selectedIntGroups.flatMap((g) => g.keys),
    [selectedIntGroups],
  );

  // Per-fase rollup of the current selection (ticked collectors + ticked interventie
  // groups) — drives the "over M fases" summary and the planning flip. Collector fases
  // come from all_collectors so the fase + date are authoritative.
  const selectedByFase = useMemo(() => selectedFasesForFlip(
    (data?.all_collectors ?? [])
      .filter((c) => selected.has(c.id))
      .map((c) => ({ faseId: c.fase_id ?? collectorFases[c.id] ?? null, m2: c.collector_m2 })),
    selectedIntGroups.map((g) => g.faseId),
    fases,
    localTodayStr(),
  ), [data, selected, collectorFases, fases, selectedIntGroups]);
  const collectorFaseCount = selectedByFase.filter((f) => f.count > 0).length;
  // Today/past fases the create auto-flips; the LAST is the einde-werken target.
  const eligibleFlipIds = useMemo(() => eligibleFlipIdsOf(selectedByFase), [selectedByFase]);
  const lastFlipId = eligibleFlipIds.length ? eligibleFlipIds[eligibleFlipIds.length - 1] : null;
  // The fase an einde-werken create would mark — named in the einde-werken prompt so
  // the PL sees which fase closes, and null when the selection has none to flip.
  const eindeFaseName = selectedByFase.find((f) => f.id === lastFlipId)?.name ?? null;

  // Which m² every percentage here is calculated on. The denominator itself
  // (data.legplan_m2) is resolved SERVER-side — legplan, else offerte m² — and this
  // only rebuilds the warning around it from the two raw numbers.
  const m2Basis = useMemo(
    () => resolveM2Basis(data?.legplan_m2_stored ?? 0, data?.offerte_m2 ?? 0),
    [data],
  );

  // Auto-build the omschrijving from the selection. Regenerate ONLY while the
  // field is still pristine (empty or equal to the last auto value) — once the
  // PL edits it, their text is preserved. (Mirrors the legacy form.)
  useEffect(() => {
    const auto = buildOmschrijving(selectedCollectors, data?.legplan_m2 ?? 0, eindeWerken, [], selectedIntGroups, m2Basis.source, m2OpFactuur, lpOpFactuur);
    setOmschrijving((prev) => (prev === '' || prev === lastAutoOmschrijving ? auto : prev));
    setLastAutoOmschrijving(auto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCollectors, data?.legplan_m2, m2Basis.source, eindeWerken, selectedIntGroups, m2OpFactuur, lpOpFactuur]);

  // Auto-naam: «Einde werken» while that toggle is on, else «Vordering N». Only
  // regenerated while the field is pristine (empty or the last auto value), so a
  // manually-typed naam is preserved. Mirrors the omschrijving auto-build above.
  useEffect(() => {
    const auto = eindeWerken ? 'Einde werken' : `Vordering ${data?.next_sequence ?? 1}`;
    setCustomName((prev) => (prev === '' || prev === lastAutoName ? auto : prev));
    setLastAutoName(auto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eindeWerken, data?.next_sequence]);

  // Autogrow the omschrijving textarea to fit its content (TaskDetailPanel technique),
  // but floor the height to a ~6-line box so the empty/placeholder state stays roomy
  // (an empty textarea collapses to ~1 line and hides the placeholder otherwise).
  useEffect(() => {
    const el = omschrijvingRef.current;
    if (!el) return;
    const MIN_PX = 132; // ≈ 6 lines of text-sm/leading-snug + padding + border
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, MIN_PX)}px`;
  }, [omschrijving]);

  // Invalidate the foto-rapport preview when the collector OR interventie selection changes.
  useEffect(() => {
    if (!fotoKey) return;
    const ids = Array.from(selected);
    const sameCollectors =
      fotoSelectionIds.length === ids.length &&
      fotoSelectionIds.every((id) => selected.has(id));
    const sameInterventie =
      fotoInterventieDates.length === selectedInterventieDates.size &&
      fotoInterventieDates.every((d) => selectedInterventieDates.has(d));
    if (!sameCollectors || !sameInterventie) {
      setFotoKey(null);
      setFotoUrl(null);
      setFotoZipUrl(null);
      setFotoSelectionIds([]);
      setFotoInterventieDates([]);
      setFotoError(null);
    }
  }, [selected, selectedInterventieDates, fotoKey, fotoSelectionIds, fotoInterventieDates]);

  // Indicative estimate only — definitive amount is computed server-side.
  const estimate = useMemo(() => {
    if (!data) return 0;
    if (eindeWerken) return Math.max(0, (data.so_line_amount || 0) - (data.contract_gevorderd || 0));
    const legplan = data.legplan_m2 || 0;
    if (legplan <= 0) return 0;
    const m2 = selectedCollectors.reduce((sum, c) => sum + (c.collector_m2 || 0), 0);
    return (m2 / legplan) * (data.so_line_amount || 0);
  }, [data, selectedCollectors, eindeWerken]);

  // Indicative € from m² (proportional to the SO line) — used for the per-fase
  // "gevorderd · €" rollup. Definitive amounts are server-computed (this is display).
  const m2ToEur = useCallback(
    (m2: number): number => {
      const legplan = data?.legplan_m2 || 0;
      if (legplan <= 0) return 0;
      return (m2 / legplan) * (data?.so_line_amount || 0);
    },
    [data],
  );

  // Selection totals for the create-panel summary.
  const selectedM2 = useMemo(
    () => selectedCollectors.reduce((sum, c) => sum + (c.collector_m2 || 0), 0),
    [selectedCollectors],
  );

  // ----- Summary tiles -----
  // Gevorderd / progress bar / Openstaand are all scoped to the pinned contract SO
  // (contract_gevorderd) so the four tiles stay consistent on ONE SO. Project-wide
  // cumulative_amount is no longer used for display. Gefactureerd stays the posted
  // SO-scoped invoiced amount. Client meerprijs (other SOs) is shown separately and
  // is NOT part of this math.
  // ----- Project-wide summary (tiles + bar) -----
  // All scoped to the NET project contract (contract_total = Σ all SOs, a minprijs
  // nets negative), NOT the base SO line: a −€20k minprijs (Blancefloerlaan) would
  // otherwise leave the bar stuck at ~45% on a fully-billed project. so_line_amount
  // stays for the vordering create-flow math above — display only changes here.
  const soLine = data?.so_line_amount ?? 0;               // base SO line — per-vordering-row % only
  const contractTotal = data?.contract_total ?? 0;        // contract netto (denominator)
  const gefactureerd = data?.gefactureerd_total ?? 0;     // all SOs invoiced, deduped (green)
  const gevorderd = data?.contract_gevorderd ?? 0;        // vordering milestones (base) — marker only
  const openstaand = Math.max(0, contractTotal - gefactureerd); // nog te factureren op het project
  const breakdown = data?.saleOrdersBreakdown ?? [];
  const isMultiSo = breakdown.length > 1;
  const pctOfContract = (n: number) => (contractTotal > 0 ? Math.min(100, Math.max(0, (n / contractTotal) * 100)) : 0);
  const gevorderdPct = pctOfContract(gevorderd);
  // Green = gefactureerd on its own scale (capped 100%). NOT clamped to the Gevorderd
  // marker: a project invoiced directly — without vordering milestones — is still
  // fully invoiced and must show a green bar, not 0%.
  const gefactureerdPct = pctOfContract(gefactureerd);
  // Blue = vordered milestones not yet invoiced (0 when invoiced ≥ vordered).
  const gevorderdNotInvoicedPct = Math.max(0, gevorderdPct - gefactureerdPct);
  // Readout tracks the bar's actual filled extent (the further of vordered / invoiced).
  const billedProgress = Math.max(gevorderd, gefactureerd);

  // Surface the contract netto + gefactureerd-% up to the collapsed section header
  // (no duplicate fetch). Null until the first load resolves so the header stays bare.
  useEffect(() => {
    onSummaryChange?.(data ? { contractNetto: contractTotal, gefactureerdPct } : null);
  }, [data, contractTotal, gefactureerdPct, onSummaryChange]);

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleLine = (id: number) => {
    setLineEntries((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id); else next.set(id, { ...emptyLineEntry });
      return next;
    });
  };

  const onLineEntryChange = (id: number, patch: Partial<LineEntry>) => {
    setLineEntries((prev) => {
      const current = prev.get(id) ?? emptyLineEntry;
      const next = new Map(prev);
      next.set(id, { ...current, ...patch });
      return next;
    });
  };

  // The m²-derived suggestion for the line-picker preview — same formula as
  // `estimate` above but NOT einde-werken-adjusted (spec: always totalM2/legplan).
  const linesAutoPct = useMemo(() => {
    const legplan = data?.legplan_m2 || 0;
    // Whole percent — the API bills whole percent, so the suggestion must be one too.
    return legplan > 0 ? wholePercent((selectedM2 / legplan) * 100) : 0;
  }, [data, selectedM2]);

  // A vordering bills against the contract when it has collectors selected, is an
  // einde-werken close-out, OR the PL has manually checked ≥1 line (a lines-only
  // vordering, no collectors) — an interventie-only vordering never does.
  const billsAgainstContract = selected.size > 0 || eindeWerken || lineEntries.size > 0;
  const billableLines = data?.billable_lines ?? [];

  // Einde werken bills the WHOLE contract closeout — re-seed on an off→on toggle
  // (never on→off, which leaves the PL's own selection untouched) so ticking
  // "Einde werken" always defaults to every open line, not just whichever the
  // PL happened to have checked already. ADDS missing headroom lines with an
  // empty entry; never touches an already-checked line's % or description.
  useEffect(() => {
    const wasOn = prevEindeWerkenRef.current;
    prevEindeWerkenRef.current = eindeWerken;
    if (!eindeWerken || wasOn) return;
    setLineEntries((prev) => {
      const missing = billableLines.filter((l) => hasHeadroom(l) && !prev.has(l.id));
      if (missing.length === 0) return prev;
      const next = new Map(prev);
      for (const l of missing) next.set(l.id, { ...emptyLineEntry });
      return next;
    });
  }, [eindeWerken, billableLines]);

  // A line is only REQUIRED (block with "Selecteer minstens één offertelijn.")
  // when collectors are selected or this is einde-werken — a PL who checks a
  // line themselves has already satisfied it, and interventie-only never needs one.
  const linesRequired = selected.size > 0 || eindeWerken;
  const linesMissing = linesRequired && billableLines.length > 0 && lineEntries.size === 0;
  // A checked line left at an empty % with no collectors to derive a suggestion
  // from resolves to 0% → bills €0. Flag it (mirrors the picker's own per-row
  // "emptyPct" check, so the host and the picker agree on which entries are bad).
  const linesPctInvalid =
    !eindeWerken && linesAutoPct <= 0 && Array.from(lineEntries.values()).some((e) => e.percentage.trim() === '');

  // This vordering bills every open offertelijn down to zero headroom — the shape
  // of a close-out. With the type toggle still on «Gevorderd» that is almost always
  // the forgotten toggle, so submit asks first (EindeWerkenPrompt) instead of
  // creating a «Vordering N» that silently ends the project's billing.
  const closesAllLines = useMemo(
    () => !eindeWerken && closesOutAllLines(billableLines, lineEntries, linesAutoPct),
    [eindeWerken, billableLines, lineEntries, linesAutoPct],
  );
  // What the prompt shows: how many lines it closes and the euro it bills — the
  // picker's own preview total, from the same rule, so the two never disagree.
  const closeOutLines = useMemo(
    () => billableLines.filter((l) => hasHeadroom(l) && lineEntries.has(l.id)),
    [billableLines, lineEntries],
  );
  const closeOutTotal = useMemo(
    () => closeOutLines.reduce((sum, l) => sum + lineBillingPreview(l, lineEntries.get(l.id), linesAutoPct, false).amount, 0),
    [closeOutLines, lineEntries, linesAutoPct],
  );

  const toggleInterventie = (date: string) => {
    setSelectedInterventieDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date); else next.add(date);
      return next;
    });
  };

  const errorMessage = (err: unknown): string => {
    if (err instanceof ApiError) {
      if (err.code === 'PROJECT_FULLY_INVOICED') return 'Dit project is al volledig gefactureerd.';
      if (err.code === 'COLLECTOR_ALREADY_VORDERED') return 'Eén of meer collectoren zitten al in een vordering.';
      return err.message;
    }
    return err instanceof Error ? err.message : 'Actie mislukt';
  };

  const handleGenerateFoto = async () => {
    const ids = Array.from(selected);
    // Interventie-only is a valid rapport (repair photos, no collectors).
    if (ids.length === 0 && interventiePhotoKeys.length === 0) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setFotoGenerating(true);
    setFotoError(null);
    setFotoKey(null);
    setFotoUrl(null);
    setFotoZipUrl(null);
    try {
      const result = await previewFotoRapport(projectId, ids, interventiePhotoKeys);
      if (ctrl.signal.aborted) return;
      setFotoKey(result.foto_rapport_key);
      setFotoUrl(result.foto_rapport_url);
      setFotoZipUrl(result.zip_url ?? null);
      setFotoSelectionIds(ids);
      setFotoInterventieDates(Array.from(selectedInterventieDates));
    } catch (err) {
      if (ctrl.signal.aborted) return;
      setFotoError(err instanceof Error ? err.message : 'Foto rapport genereren mislukt');
    } finally {
      if (!ctrl.signal.aborted) setFotoGenerating(false);
    }
  };

  /**
   * Create the vordering.
   *
   * `answered` carries the PL's reply to the «is dit einde werken?» question and
   * is the ONLY way past that prompt: an unanswered submit that closes out every
   * offertelijn opens the prompt and returns. An answer of einde werken also
   * re-derives the naam/omschrijving (only while they are still the pristine auto
   * text) so the record is not called «Vordering N» while it closes the project.
   */
  const submit = async (answered?: { eindeWerken: boolean }) => {
    setLinesTouched(true);
    if (linesMissing || linesPctInvalid) return;
    if (!answered && closesAllLines) {
      setEindeWerkenAsk(true);
      return;
    }
    const isEinde = answered?.eindeWerken ?? eindeWerken;
    // The prompt flipped the type after the form was already filled in, so the auto
    // text has to catch up here — a manually-typed naam/omschrijving stays untouched
    // (same pristine guard as the toggle's own auto-build effects).
    const nameOut = isEinde !== eindeWerken && customName === lastAutoName
      ? 'Einde werken'
      : customName;
    const omschrijvingOut = isEinde !== eindeWerken && omschrijving === lastAutoOmschrijving
      ? buildOmschrijving(selectedCollectors, data?.legplan_m2 ?? 0, isEinde, [], selectedIntGroups, m2Basis.source, m2OpFactuur, lpOpFactuur)
      : omschrijving;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitWarning(null);
    try {
      const lineBilling: LineBillingEntry[] = Array.from(lineEntries.entries()).map(([line_id, entry]) => {
        const pctNum = entry.percentage.trim() === '' ? NaN : Number(entry.percentage);
        return {
          line_id,
          // Einde werken bills the full remaining regardless — omit the % so the
          // server's own einde-werken handling decides (mirrors the old flag).
          percentage: isEinde || Number.isNaN(pctNum)
            ? undefined
            : wholePercent(Math.min(100, Math.max(0, pctNum))),
          description: entry.description.trim() || undefined,
        };
      });
      const { vordering: created } = await createVordering(projectId, {
        collector_ids: Array.from(selected),
        einde_werken: isEinde,
        custom_name: nameOut.trim() || undefined,
        omschrijving: omschrijvingOut.trim() || undefined,
        notes: notes.trim() || undefined,
        signer_name: signerName.trim() || undefined,
        // Derived intersection, not the raw Set — a stale date resolves to no keys.
        interventie_photo_keys: interventiePhotoKeys.length > 0 ? interventiePhotoKeys : undefined,
        foto_rapport_key: fotoKey ?? undefined,
        include_foto_link: includeFotoLink && !!fotoKey,
        line_billing: lineBilling.length > 0 ? lineBilling : undefined,
      });

      // The photo mail to the customer, at once (Igor 2026-10-21): only when the
      // vordering has a foto-rapport. The picked contact's email prefills it.
      if (created?.id && created.foto_rapport_url) {
        setPhotoMailFor({ id: created.id, opener: { from: 'wizard', email: signerContact?.email ?? null, contactPicked: signerContact !== null } });
      }

      // ──── FLIP THE PLANNING BOARD (ADR-064 — supersedes ADR-052 "guide-only"
      // for the dashboard vordering flow) ────
      // The today/past fases we just vordered move to «gevorderd»; with the Einde
      // werken toggle the LAST (latest-planned) fase becomes «einde werken» instead
      // — the earlier fases stay/turn gevorderd. Re-marking an already-gevorderd
      // fase is a harmless no-op. Best-effort: a marking failure is a non-fatal
      // warning (the vordering already exists), so the create itself never rolls back.
      if (eligibleFlipIds.length > 0) {
        const eindeWerkenId = isEinde ? eligibleFlipIds[eligibleFlipIds.length - 1] : null;
        const gevorderdIds = isEinde ? eligibleFlipIds.slice(0, -1) : eligibleFlipIds;
        try {
          if (gevorderdIds.length > 0) {
            await markFasesGevorderd(projectId, gevorderdIds, 'gevorderd');
          }
          if (eindeWerkenId !== null) {
            await markFasesGevorderd(projectId, [eindeWerkenId], 'einde_werken');
          }
        } catch (markErr) {
          setSubmitWarning(
            `Vordering aangemaakt, maar de planning automatisch bijwerken is mislukt: ${errorMessage(markErr)}. Markeer de fases handmatig in de planning.`,
          );
        }
      } else if (isEinde) {
        // Einde werken with no today/past fase in the selection — e.g. at project end
        // every collector was already vordered, so none is ticked. Nothing to flip;
        // tell the PL explicitly instead of silently skipping the «einde werken» marker.
        setSubmitWarning(
          'Vordering aangemaakt, maar er is geen fase van vandaag of het verleden geselecteerd om op «einde werken» te zetten. Markeer de eindfase handmatig in de planning.',
        );
      }

      // Reset the form for the next vordering.
      setSelected(new Set());
      setSelectedInterventieDates(new Set());
      setEindeWerken(false);
      setEindeWerkenAsk(false);
      setSignerName('');
      setSignerContact(null);
      setNotes('');
      setOmschrijving('');
      setLastAutoOmschrijving('');
      setM2OpFactuur(false);
      setLpOpFactuur(false);
      setFotoKey(null);
      setFotoUrl(null);
      setFotoZipUrl(null);
      setFotoSelectionIds([]);
      setFotoInterventieDates([]);
      setIncludeFotoLink(true);
      setLineEntries(new Map());
      setLinesTouched(false);
      // Re-run the default-selection logic on the next load: the vordering we
      // just created moves headroom, which can change which line(s) default-check.
      linesInitializedRef.current = false;
      load();
      onChanged?.();
      // Reflect the new «gevorderd»/«einde werken» labels instantly: project tasks
      // (badge) + the scheduler board (when loaded).
      void refreshProject(projectId);
      if (planningLoaded) void refreshPlanning(undefined, { silent: true });
    } catch (err) {
      setSubmitError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id: number) => {
    setActionId(id);
    setDeleteError(null);
    try {
      await deleteVordering(id);
      load();
      onChanged?.();
      // Backend reverts uncovered fases to "In uitvoering" — refresh project tasks
      // (badge) + the scheduler board so the labels reverse instantly.
      void refreshProject(projectId);
      if (planningLoaded) void refreshPlanning(undefined, { silent: true });
    } catch (err) {
      // Inline error — must NOT touch the fatal `error` state (would hide the section).
      setDeleteError(errorMessage(err));
    } finally {
      setActionId(null);
    }
  };

  const onShare = async (id: number) => {
    setShareState(null);
    try {
      const { url, zipUrl } = await getShareLink(id);
      // One paste gives the client both: the rapport and the loose photos.
      const text = zipUrl ? `Foto rapport (PDF): ${url}\nLosse foto's (ZIP): ${zipUrl}` : url;
      await navigator.clipboard?.writeText(text);
      setShareState({ id, status: 'copied' });
    } catch {
      // Either the link fetch or the clipboard write failed — surface it, don't pretend success.
      setShareState({ id, status: 'error' });
    }
  };

  // A fully-invoiced project still has open interventie work to bill — repair
  // photos are separate from the SO contract the billing-lock protects.
  const hasOpenInterventie = interventieGroupViews.some((g) => g.openKeys.length > 0);
  // Interventie-only = doesn't bill the contract (no collectors, not einde_werken,
  // no picked lines) → bills €0, so it stays allowed even when the project is
  // fully invoiced. A picked line DOES bill the contract (line_billing), so it
  // must NOT qualify as interventie-only. Mirrors the server exemption in vorderingen.ts.
  const interventieOnly = !billsAgainstContract && selectedIntGroups.length > 0;

  // Signer is OPTIONAL — submit is NOT gated on it (relaxed from the old `!!signerId`).
  // Interventie-only vorderingen are valid; gate on the DERIVED groups (never the raw
  // Set) so a stale date can't enable an empty create.
  const canSubmit =
    canWrite &&
    !submitting &&
    !fotoGenerating &&
    !linesMissing &&
    !linesPctInvalid &&
    (interventieOnly || !fullyInvoiced) &&
    (eindeWerken || selected.size > 0 || selectedIntGroups.length > 0 || lineEntries.size > 0);

  const Wrapper = embedded ? 'div' : 'section';
  return (
    <Wrapper
      aria-label="Vordering"
      className={embedded ? 'space-y-5' : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-5'}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {!embedded && <h3 className="text-base font-extrabold text-td-navy">Vordering</h3>}
          <span className="rounded-full bg-td-blue/10 px-2 py-0.5 text-[11px] font-bold text-td-blue">Hele project · alle fases</span>
        </div>
        {data && (
          <span className="text-xs text-td-light">Contract netto: {fmtEur(contractTotal)} (excl. btw)</span>
        )}
      </div>

      {loading && !data ? (
        <p className="text-sm text-td-muted">Vorderingen laden…</p>
      ) : error ? (
        <p className="text-sm text-td-magenta">Vorderingen laden mislukt: {error}</p>
      ) : (
        <>
          {/* ===== Summary tiles + stacked progress bar (project-wide, net contract) ===== */}
          <div>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">Gevorderd</p>
                <p className="mt-1 text-lg font-extrabold text-td-navy">{fmtEur(gevorderd)}</p>
                <p className="text-[11px] text-td-light">door PL · {Math.round(gevorderdPct)}% van contract</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">Gefactureerd</p>
                <p className="mt-1 text-lg font-extrabold text-td-green">{fmtEur(gefactureerd)}</p>
                <p className="text-[11px] text-td-light">
                  {(data?.invoices ?? []).length} {(data?.invoices ?? []).length === 1 ? 'factuur' : 'facturen'} · echte Odoo
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">Openstaand</p>
                <p className="mt-1 text-lg font-extrabold text-td-magenta">{fmtEur(openstaand)}</p>
                <p className="text-[11px] text-td-light">contract netto − gefactureerd</p>
              </div>
            </div>
            <div className="mt-3">
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
                <div className="flex h-full">
                  <div className="h-full bg-td-green" style={{ width: `${gefactureerdPct}%` }} />
                  <div className="h-full bg-td-blue/60" style={{ width: `${gevorderdNotInvoicedPct}%` }} />
                </div>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-4 text-[11px] text-td-muted">
                <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-td-green" />Gefactureerd</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-td-blue/60" />Gevorderd (nog niet gefact.)</span>
                <span className="ml-auto">{fmtEur(billedProgress)} / {fmtEur(contractTotal)}</span>
              </div>
            </div>

            {/* Per-SO contract breakdown (multi-SO only). Replaces the old collapsed
                "Meerprijs (apart gefactureerd)" block: one row per customer SO with a
                colour-coded role tag (Basis blue / Meerprijs purple / Minprijs red),
                the client_order_ref as the bold label, the S-number + date underneath,
                and the SO's own contract (negative = minprijs) + invoiced totals. */}
            {isMultiSo && (
              <div className="mt-3 overflow-hidden rounded-xl border border-gray-200">
                <div className="grid grid-cols-[88px_1fr_auto] gap-3 bg-gray-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-td-muted sm:grid-cols-[96px_1fr_110px_110px]">
                  <span>Type</span>
                  <span>Omschrijving</span>
                  <span className="text-right">Contract</span>
                  <span className="hidden text-right sm:block">Gefactureerd</span>
                </div>
                <ul className="divide-y divide-gray-100">
                  {breakdown.map((so) => (
                    <SoBreakdownRow key={so.id} so={so} />
                  ))}
                </ul>
              </div>
            )}
            {/* SO-level invoices — attribution is verkooporder-wide, NOT per vordering-row,
                so we render this ONCE here instead of inside every history card.
                Multi-SO projects show the invoices per order inside the breakdown above
                (where a consolidated invoice is split across the orders it bills), so
                this flat list is only for single-SO projects, which render no breakdown. */}
            {!isMultiSo && (data?.invoices ?? []).length > 0 && (
              <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] text-td-muted">
                <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">Facturen (verkooporder)</p>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                  {(data?.invoices ?? []).map((inv, i) => (
                    <span key={`${inv.name}-${i}`} className="flex items-center gap-1">
                      <span className="font-semibold text-td-navy">{inv.name}</span> · {fmtEur(inv.amount)} · {fmtDate(inv.date)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {fullyInvoiced && (
            <p className="rounded-lg bg-green-50 px-3 py-2 text-sm font-semibold text-td-green">
              Dit project is volledig gefactureerd
              {hasOpenInterventie
                ? ' — enkel interventies kunnen nog gevorderd worden.'
                : ' — er kan geen vordering meer aangemaakt worden.'}
            </p>
          )}

          {/* ===== Cross-fase overzicht — collectoren per fase ===== */}
          {canWrite && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-extrabold uppercase tracking-wide text-td-navy">Collectoren per fase</p>
                <p className="text-[11px] text-td-light">vink aan wat je wil vorderen</p>
              </div>
              {faseGroups.length === 0 ? (
                <p className="text-sm text-td-muted">Geen collectoren op druk in dit project.</p>
              ) : (
                faseGroups.map((g) => (
                  <FaseGroupCard
                    key={g.id ?? 'geen-fase'}
                    group={g}
                    selected={selected}
                    onToggle={toggle}
                    faseEur={m2ToEur(g.gevorderdM2)}
                  />
                ))
              )}
            </div>
          )}

          {/* ===== Interventies — date-groups selectable into this vordering ===== */}
          {/* Hidden entirely when the project has no interventie photos. Ticking a
              date-group folds its repair photos into the vordering (foto rapport +
              omschrijving); no kringen/m² — interventie has no fase to flip. */}
          {canWrite && interventieGroupViews.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-extrabold uppercase tracking-wide text-td-navy">Interventies</p>
                <p className="text-[11px] text-td-light">interventiefoto's per dag</p>
              </div>
              <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
                {interventieGroupViews.map((g) => {
                  const fullyVordered = g.openKeys.length === 0;
                  const checked = selectedInterventieDates.has(g.id);
                  if (fullyVordered) {
                    // Gevorderd: no checkbox, green dot + the vordering name(s) — mirrors a vordered collector row.
                    return (
                      <li key={g.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                        <span className="pl-6 text-td-navy">{g.title} <span className="text-td-muted">· {fotoCount(g.total)}</span></span>
                        <div className="flex shrink-0 items-center gap-2">
                          <span className="flex items-center gap-1 text-[11px] font-semibold text-td-green">
                            <span className="inline-block h-2 w-2 rounded-full bg-td-green" aria-hidden="true" />Gevorderd
                          </span>
                          {g.vorderingNames.map((n) => (
                            <span key={n} className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-td-muted">{n}</span>
                          ))}
                        </div>
                      </li>
                    );
                  }
                  return (
                    <li key={g.id} className={`flex items-center justify-between gap-2 px-3 py-2 text-sm ${checked ? 'bg-td-blue/5' : ''}`}>
                      <label className="flex items-center gap-2 text-td-navy">
                        <input type="checkbox" checked={checked} onChange={() => toggleInterventie(g.id)} className="accent-td-blue" />
                        {g.title}
                      </label>
                      <div className="flex shrink-0 items-center gap-2 text-[11px] font-semibold text-td-muted">
                        {/* Partially vordered — some photos already billed, the rest still open. */}
                        {g.openKeys.length < g.total && (
                          <span className="text-td-green">{g.total - g.openKeys.length} gevorderd</span>
                        )}
                        <span>{fotoCount(g.openKeys.length)}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* ===== Create panel ===== */}
          {/* Fully-invoiced projects keep the panel ONLY when open interventie work
              remains — interventie-only submits stay valid (canSubmit / server). */}
          {canWrite && (!fullyInvoiced || hasOpenInterventie) && (
            <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-extrabold uppercase tracking-wide text-td-navy">
                  Nieuwe vordering
                  {selected.size > 0 && (
                    <span className="font-semibold normal-case text-td-muted"> · {selected.size} collectoren geselecteerd</span>
                  )}
                  {selectedIntGroups.length > 0 && (
                    <span className="font-semibold normal-case text-td-muted"> · {selectedIntGroups.length} interventie{selectedIntGroups.length === 1 ? '' : 's'}</span>
                  )}
                </p>
                <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5 text-xs font-semibold" role="group" aria-label="Type vordering">
                  <button
                    type="button"
                    aria-pressed={!eindeWerken}
                    onClick={() => setEindeWerken(false)}
                    className={`rounded-md px-3 py-1 ${!eindeWerken ? 'bg-td-blue text-white' : 'text-td-muted'}`}
                  >
                    Gevorderd
                  </button>
                  <button
                    type="button"
                    aria-pressed={eindeWerken}
                    onClick={() => setEindeWerken(true)}
                    className={`rounded-md px-3 py-1 ${eindeWerken ? 'bg-td-blue text-white' : 'text-td-muted'}`}
                  >
                    Einde werken (rest)
                  </button>
                </div>
              </div>
              <p className="-mt-2 text-[11px] text-td-light">
                Alle velden hieronder zijn ingevuld als <span className="font-semibold">suggestie</span> — pas gerust elk aspect aan.
              </p>

              {/* Selectie-samenvatting + planning-flip PREVIEW (collectors and/or
                  interventie groups). */}
              {(selected.size > 0 || selectedIntGroups.length > 0) && (
                <div className="space-y-3">
                  {selected.size > 0 && (
                    <p className="text-[11px] text-td-muted">
                      <span className="font-semibold text-td-navy">{selectedCollectors.length} collectoren</span>
                      {' · '}{Math.round(selectedM2)} m²
                      {collectorFaseCount > 0
                        ? ` · over ${collectorFaseCount} ${collectorFaseCount === 1 ? 'fase' : 'fases'}`
                        : ' · geen gekoppelde fase'}
                    </p>
                  )}
                  <PlanningFlipPreview
                    fases={selectedByFase}
                    lastFlipId={lastFlipId}
                    eindeWerken={eindeWerken}
                    hasCollectors={selected.size > 0}
                  />
                </div>
              )}

              {/* Te vorderen offertelijnen — always shown when the contract has
                  billable lines, even with no collectors selected yet: a PL can
                  build a lines-only vordering purely from picked lines + text. */}
              {billableLines.length > 0 && (
                <LineBillingPicker
                  lines={billableLines}
                  entries={lineEntries}
                  onToggleLine={toggleLine}
                  onEntryChange={onLineEntryChange}
                  autoPercentage={linesAutoPct}
                  m2Notice={<M2BasisNotice basis={m2Basis} />}
                  eindeWerken={eindeWerken}
                  showRequiredError={linesTouched && linesMissing}
                  showPercentageWarning={linesTouched}
                />
              )}

              {/* 1 · Wat te vorderen */}
              <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3">
                <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">
                  1 · Wat te vorderen <span className="font-normal normal-case text-td-light">— deze tekst gaat naar Odoo</span>
                </p>
                <div>
                  <label htmlFor="vord-name" className="block text-[10px] font-semibold text-td-muted">Naam</label>
                  <input
                    id="vord-name"
                    type="text"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm font-semibold text-td-navy focus:border-td-blue focus:outline-none focus:ring-1 focus:ring-td-blue"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <label htmlFor="vord-omschrijving" className="block text-[10px] font-semibold text-td-muted">Omschrijving</label>
                    <span className="text-[10px] text-td-light">automatisch opgesteld · bewerkbaar ✎</span>
                  </div>
                  <textarea
                    id="vord-omschrijving"
                    ref={omschrijvingRef}
                    value={omschrijving}
                    onChange={(e) => setOmschrijving(e.target.value)}
                    rows={4}
                    placeholder="Beschrijf wat er gevorderd wordt…"
                    className="mt-1 w-full resize-none overflow-hidden rounded border border-gray-300 px-2 py-1.5 text-sm font-mono leading-snug text-td-navy focus:border-td-blue focus:outline-none focus:ring-1 focus:ring-td-blue"
                  />
                  <OmschrijvingOptions m2={m2OpFactuur} lp={lpOpFactuur} onM2Change={setM2OpFactuur} onLpChange={setLpOpFactuur} />
                </div>
                <div>
                  <label htmlFor="vord-notes" className="block text-[10px] font-semibold text-td-muted">Opmerkingen (optioneel)</label>
                  <input
                    id="vord-notes"
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Bv. meerprijs extra zone"
                    className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm text-td-navy focus:border-td-blue focus:outline-none focus:ring-1 focus:ring-td-blue"
                  />
                </div>
              </div>

              {/* 2 · Foto rapport */}
              <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">2 · Foto rapport</p>
                  <button
                    type="button"
                    onClick={() => void handleGenerateFoto()}
                    disabled={fotoGenerating || submitting || (selected.size === 0 && selectedIntGroups.length === 0)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-td-blue px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-td-blue/90 disabled:opacity-40"
                  >
                    {fotoGenerating ? (
                      <>
                        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                        Genereren…
                      </>
                    ) : (
                      'Genereer foto rapport'
                    )}
                  </button>
                </div>
                {selected.size === 0 && selectedIntGroups.length === 0 && !fotoKey && (
                  <p className="text-[11px] text-td-light">Selecteer eerst één of meer collectoren of interventies.</p>
                )}
                {fotoKey && fotoUrl && (
                  <div className="space-y-2 rounded-lg bg-gray-50 p-2">
                    <a
                      href={fotoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-td-blue hover:underline"
                    >
                      📄 Foto rapport bekijken
                    </a>
                    {fotoZipUrl ? (
                      <a
                        href={fotoZipUrl}
                        className="ml-4 inline-flex items-center gap-1.5 text-xs font-semibold text-td-blue hover:underline"
                      >
                        🗂️ Losse foto's (ZIP) downloaden
                      </a>
                    ) : (
                      <p className="text-[11px] text-td-light">Geen losse foto's (ZIP): er zijn geen foto's gevonden of de ZIP kon niet gemaakt worden.</p>
                    )}
                    <label className="flex items-center gap-2 text-xs font-semibold text-td-navy">
                      <input
                        type="checkbox"
                        checked={includeFotoLink}
                        onChange={(e) => setIncludeFotoLink(e.target.checked)}
                        className="accent-td-blue"
                      />
                      {fotoZipUrl ? "Foto rapport en losse foto's meesturen met de vordering" : 'Foto rapport meesturen met de vordering'}
                    </label>
                  </div>
                )}
                {fotoError && <p className="text-xs text-td-magenta" role="alert">{fotoError}</p>}
              </div>

              {/* 3 · Ondertekenaar */}
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">3 · Ondertekenaar</p>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                  <input
                    type="text"
                    aria-label="Ondertekenaar"
                    value={signerName}
                    onChange={(e) => { setSignerName(e.target.value); setSignerContact(null); }}
                    placeholder="Naam of e-mail (optioneel)"
                    className="flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm text-td-navy focus:border-td-blue focus:outline-none focus:ring-1 focus:ring-td-blue"
                  />
                  <select
                    aria-label="Kies contact"
                    value=""
                    onChange={(e) => {
                      // Fill ONLY the clean partner name — the email stays in the option label
                      // for disambiguation but must NOT leak onto the vorderingsstaat / sign
                      // template / Facturatie task. A PL who wants email signing types it manually.
                      const c = (data?.client_contacts ?? []).find((x) => String(x.id) === e.target.value);
                      if (c) { setSignerName(c.name); setSignerContact({ email: c.email || null }); }
                      e.target.value = '';
                    }}
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm text-td-muted sm:w-56"
                  >
                    <option value="">Kies contact om in te vullen…</option>
                    {(data?.client_contacts ?? []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.email ? ` — ${c.email}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="mt-1 text-[11px] text-td-light">Optioneel — laat leeg, typ een naam/e-mail, of kies een contact.</p>
              </div>

              {/* Footer: estimate + submit */}
              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 pt-3">
                <div className="mr-auto flex items-center gap-2 rounded-lg bg-td-blue/5 px-3 py-1.5">
                  <span className="text-[11px] text-td-muted">Geschat bedrag</span>
                  <span className="text-sm font-extrabold text-td-navy">{fmtEur(estimate)}</span>
                  <span className="text-[10px] text-td-light">(definitief = server)</span>
                </div>
                <button
                  type="button"
                  disabled={!canSubmit}
                  onClick={() => void submit()}
                  className="rounded-lg bg-td-blue px-4 py-2 text-sm font-semibold text-white transition hover:bg-td-blue/90 disabled:opacity-40"
                >
                  {submitting ? 'Bezig…' : 'Vordering aanmaken'}
                </button>
              </div>
              {submitError && <p className="text-sm text-td-magenta" role="alert">{submitError}</p>}
              {submitWarning && <p className="text-sm text-amber-600" role="status">{submitWarning}</p>}

              {eindeWerkenAsk && (
                <EindeWerkenPrompt
                  lineCount={closeOutLines.length}
                  total={closeOutTotal}
                  eindeFaseName={eindeFaseName}
                  onEindeWerken={() => {
                    setEindeWerkenAsk(false);
                    // Flip the visible toggle too: if the create fails, the form the
                    // PL returns to must show the answer they just gave.
                    setEindeWerken(true);
                    void submit({ eindeWerken: true });
                  }}
                  onGewoon={() => {
                    setEindeWerkenAsk(false);
                    void submit({ eindeWerken: false });
                  }}
                  onCancel={() => setEindeWerkenAsk(false)}
                />
              )}
            </div>
          )}

          {/* ===== Historie ===== */}
          <div>
            <p className="text-xs font-extrabold uppercase tracking-wide text-td-navy">Historie</p>
            {deleteError && <p className="mt-1 text-sm text-td-magenta" role="alert">{deleteError}</p>}
            {(data?.vorderingen ?? []).length === 0 ? (
              <p className="mt-2 text-sm text-td-muted">Nog geen vorderingen.</p>
            ) : (
              <div className="mt-2 space-y-2">
                {(data?.vorderingen ?? []).map((v) => (
                  <VorderingHistoryCard
                    key={v.id}
                    v={v}
                    soLine={soLine}
                    canWrite={canWrite}
                    deleting={actionId === v.id}
                    shareStatus={shareState?.id === v.id ? shareState.status : null}
                    onShare={() => void onShare(v.id)}
                    onDelete={() => void onDelete(v.id)}
                    photoMailRefresh={photoMailRefresh}
                    onPhotoMail={() => setPhotoMailFor({ id: v.id, opener: { from: 'card' } })}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}
      {photoMailFor && (
        <PhotoMailPrompt
          vorderingId={photoMailFor.id}
          opener={photoMailFor.opener}
          onClose={() => setPhotoMailFor(null)}
          onChanged={() => setPhotoMailRefresh((n) => n + 1)}
        />
      )}
    </Wrapper>
  );
}

/**
 * One bordered fase card in the cross-fase overview (mockup :76-173). Header =
 * fase name + planned-date badge + "N/M gevorderd · €". Rows split into Gevorderd
 * (no checkbox, "Vordering N" tag) and Niet-gevorderd (checkbox, selectable).
 * Toekomst fases get an amber framing + a "toekomst" badge per row.
 */
function FaseGroupCard({
  group, selected, onToggle, faseEur,
}: {
  group: FaseGroup;
  selected: Set<number>;
  onToggle: (id: number) => void;
  faseEur: number;
}) {
  const { name, date, isFuture, rows, gevorderdCount, total } = group;
  return (
    <div className={`rounded-xl border bg-white ${isFuture ? 'border-amber-200' : 'border-gray-200'}`}>
      <div className={`flex flex-wrap items-center justify-between gap-2 rounded-t-xl px-3 py-2 ${isFuture ? 'bg-amber-50' : 'bg-gray-50'}`}>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-td-navy">{name}</span>
          {isFuture ? (
            <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-bold text-amber-800">
              ⚠ gepland {fmtDateShort(date)} · toekomst
            </span>
          ) : (
            <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold text-td-muted">
              gepland {fmtDateShort(date)}
            </span>
          )}
        </div>
        <span className="text-[11px] font-semibold text-td-muted">
          {gevorderdCount} / {total} gevorderd{gevorderdCount > 0 ? ` · ${fmtEur(faseEur)}` : ''}
        </span>
      </div>
      <ul className="divide-y divide-gray-100">
        {rows.map((c) => (
          <CollectorOverviewRow
            key={c.id}
            c={c}
            checked={selected.has(c.id)}
            onToggle={() => onToggle(c.id)}
            isFuture={isFuture}
          />
        ))}
      </ul>
    </div>
  );
}

function CollectorOverviewRow({
  c, checked, onToggle, isFuture,
}: {
  c: AllCollector;
  checked: boolean;
  onToggle: () => void;
  isFuture: boolean;
}) {
  const foto = fotoBadge(c.fotos_uitvoering);
  const m2 = `· ${Math.round(c.collector_m2)} m²`;

  if (c.vordered) {
    // Gevorderd: no checkbox, green dot + "Vordering N" tag.
    return (
      <li className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
        <div className="flex items-center gap-2 pl-6">
          <span className="text-td-navy">{c.name} <span className="text-td-muted">{m2}</span></span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {foto && (
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${foto.className}`}>{foto.label}</span>
          )}
          <span className="flex items-center gap-1 text-[11px] font-semibold text-td-green">
            <span className="inline-block h-2 w-2 rounded-full bg-td-green" aria-hidden="true" />Gevorderd
          </span>
          {c.vordering_name && (
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-td-muted">{c.vordering_name}</span>
          )}
        </div>
      </li>
    );
  }

  // Niet gevorderd: checkbox + grey-ring dot. Selected rows highlight; toekomst gets amber tint.
  const rowBg = checked ? (isFuture ? 'bg-amber-50/60' : 'bg-td-blue/5') : '';
  return (
    <li className={`flex items-center justify-between gap-2 px-3 py-2 text-sm ${rowBg}`}>
      <label className="flex items-center gap-2 text-td-navy">
        <input type="checkbox" checked={checked} onChange={onToggle} className="accent-td-blue" />
        {c.name} <span className="text-td-muted">{m2}</span>
      </label>
      <div className="flex shrink-0 items-center gap-2">
        {foto && (
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${foto.className}`}>{foto.label}</span>
        )}
        {isFuture && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">⚠ toekomst</span>
        )}
        <span className="flex items-center gap-1 text-[11px] font-semibold text-td-muted">
          <span className="inline-block h-2 w-2 rounded-full ring-1 ring-inset ring-td-light" aria-hidden="true" />Niet gevorderd
        </span>
      </div>
    </li>
  );
}

/**
 * One row in the per-SO contract breakdown (mockup `.sorow`). Role tag + the
 * client_order_ref as the bold label (falls back to the S-number when missing),
 * the S-number · date underneath, then the SO's net contract (negative = minprijs,
 * rendered red) and its invoiced total.
 */
function SoBreakdownRow({ so }: { so: SaleOrderBreakdown }) {
  const tag = SO_ROLE_TAG[so.role] ?? SO_ROLE_TAG.meerprijs;
  const label = so.client_order_ref?.trim() || so.name;
  const negative = so.amount_untaxed < 0;
  return (
    <li className={`px-3 py-2.5 ${so.role === 'basis' ? 'bg-td-blue/5' : ''}`}>
      <div className="grid grid-cols-[88px_1fr_auto] items-center gap-3 sm:grid-cols-[96px_1fr_110px_110px]">
        <div>
          <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${tag.className}`}>
            {tag.label}
          </span>
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-td-navy" title={label}>{label}</p>
          <p className="mt-0.5 text-[11px] text-td-muted">
            <span className="font-mono">{so.name}</span> · {fmtDateDash(so.date_order)}
          </p>
        </div>
        <div className="text-right">
          <p className={`text-sm font-extrabold ${negative ? 'text-td-magenta' : 'text-td-navy'}`}>{fmtEur(so.amount_untaxed)}</p>
          <p className="text-[10px] text-td-light sm:hidden">gef. {fmtEur(so.invoiced)}</p>
        </div>
        <div className="hidden text-right sm:block">
          <p className="text-sm font-bold text-td-green">{fmtEur(so.invoiced)}</p>
        </div>
      </div>
      {/* The invoices behind THIS order's Gefactureerd — they always sum to it. A
          consolidated invoice shows under every order it bills, carrying only that
          order's share, so the same number can legitimately appear twice on screen
          under two different SOs. Credit notes come through negative. */}
      {so.invoices.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 pl-0 text-[11px] text-td-muted sm:pl-[108px]">
          {so.invoices.map((inv, i) => (
            <span key={`${inv.name}-${i}`} className="flex items-center gap-1">
              <span className="font-semibold text-td-navy">{inv.name}</span>
              <span className={inv.amount < 0 ? 'font-semibold text-td-magenta' : ''}>{fmtEur(inv.amount)}</span>
              <span className="text-td-light">· {fmtDate(inv.date)}</span>
            </span>
          ))}
        </div>
      )}
    </li>
  );
}

function VorderingHistoryCard({
  v, soLine, canWrite, deleting, shareStatus, onShare, onDelete, photoMailRefresh, onPhotoMail,
}: {
  v: Vordering;
  soLine: number;
  canWrite: boolean;
  deleting: boolean;
  shareStatus: 'copied' | 'error' | null;
  onShare: () => void;
  onDelete: () => void;
  photoMailRefresh: number;
  onPhotoMail: () => void;
}) {
  const pctOfSo = soLine > 0 ? Math.round((v.total_amount / soLine) * 100) : Math.round(v.percentage * 100);
  // The photo-mail lines load only for an opened card (one call per card, not per list).
  const [open, setOpen] = useState(false);
  return (
    <details
      className="group rounded-lg border border-gray-200 bg-white p-3 [&_summary]:list-none"
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <svg className="h-3.5 w-3.5 text-td-muted transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="font-bold text-td-navy">{v.name}</span>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${statusBadgeClass(v.status)}`}>{statusLabel(v.status)}</span>
          </div>
          <div className="flex items-center gap-3 text-xs">
            {v.foto_rapport_url && (
              // stopPropagation only — the anchor must still navigate/download.
              <a
                href={v.foto_rapport_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="font-semibold text-td-blue hover:underline"
              >
                📄 Foto rapport
              </a>
            )}
            {/* preventDefault + stopPropagation so the click does not toggle the parent <details>. */}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShare(); }}
              className="font-semibold text-td-blue hover:underline"
            >
              Deel-link
            </button>
            {shareStatus && (
              <span className={shareStatus === 'copied' ? 'text-td-green' : 'text-td-magenta'}>
                {shareStatus === 'copied' ? 'Gekopieerd!' : 'Mislukt'}
              </span>
            )}
            {canWrite && v.foto_rapport_url && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPhotoMail(); }}
                className="font-semibold text-td-blue hover:underline"
              >
                Foto&apos;s versturen
              </button>
            )}
            {canWrite && v.status === 'concept' && (
              <button
                type="button"
                disabled={deleting}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(); }}
                className="font-semibold text-td-magenta hover:underline disabled:opacity-40"
              >
                Verwijder
              </button>
            )}
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 pl-5 text-xs text-td-muted">
          <span>{pctOfSo}% van SO</span>
          <span className="font-semibold text-td-navy">Gevorderd {fmtEur(v.total_amount)}</span>
          <span>Aangemaakt {fmtDate(v.create_date)}</span>
        </div>
      </summary>
      <div className="mt-3 ml-5 space-y-2">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">Bericht in Odoo</p>
          {v.message ? (
            <pre className="mt-1 whitespace-pre-wrap rounded bg-gray-50 p-2 text-[11px] font-mono leading-snug text-td-navy">{v.message}</pre>
          ) : (
            <p className="mt-1 text-[11px] text-td-light">Geen bericht gevonden in Odoo.</p>
          )}
        </div>
        {open && v.foto_rapport_url && (
          <PhotoMailCardLines vorderingId={v.id} refreshKey={photoMailRefresh} canWrite={canWrite} />
        )}
      </div>
    </details>
  );
}
